import { useLingui } from '@lingui/react/macro';
import React from 'react';
import { RecorderKind } from 'modules/video-markers/domain/interfaces';
import { VideoMarkersState } from 'modules/video-markers/ui/hooks';
import { theme } from 'styles/theme/theme';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';

export interface VideoMarkersPanelProps {
  state: VideoMarkersState;
}

const RECORDER_NAMES: Record<RecorderKind, string> = { obs: 'OBS', vmix: 'vMix' };

/**
 * Блок видеометок в джойстике оверлея: подключение к программе, идёт ли запись, сколько строк
 * записано за матч и где в видео последнее событие. Зрителю на табло ничего не выводится —
 * джойстик видно только через «Взаимодействовать» в OBS или окно взаимодействия vMix.
 */
function VideoMarkersPanel({ state }: VideoMarkersPanelProps) {
  const { t } = useLingui();
  const { recorder, connection, position, counts, lastEvent, markManual } = state;
  if (recorder === null) return null;

  const name = RECORDER_NAMES[recorder];
  const isConnected = connection.status === 'connected';
  const recordingMs = position?.recordingMs ?? null;
  const streamingMs = position?.streamingMs ?? null;

  const eventCount = counts.event;
  const manualCount = counts.manual;
  const clockCount = counts.clock;

  const byClock = formatPosition(lastEvent?.position.byClockMs ?? null);
  const byReceipt = formatPosition(lastEvent?.position.byReceiptMs ?? null);
  const delayMs = lastEvent?.position.statistDelayMs ?? null;
  const delaySeconds = delayMs === null ? null : (delayMs / 1000).toFixed(1);
  const delay = delaySeconds === null ? '—' : t`${delaySeconds} с`;
  const recordingTime = formatPosition(recordingMs);
  const streamingTime = formatPosition(streamingMs);

  // Подписи — здесь, а не во вспомогательной функции: макрос `t` переводит только шаблоны,
  // где `t` — та самая переменная из `useLingui()`.
  let connectionText: string;
  switch (connection.status) {
    case 'connected':
      connectionText = t`${name} подключён`;
      break;
    case 'connecting':
      connectionText = t`Подключение к ${name}…`;
      break;
    case 'disconnected':
      connectionText = connection.reason === 'auth' ? t`Нет подключения: неверный пароль` : t`Нет подключения к ${name}`;
      break;
    default:
      connectionText = t`Нет подключения к ${name}`;
  }

  return (
    <div className={'flex flex-col gap-1 px-4 py-1.5'} style={{ background: theme.colors.Blue }} data-testid={'video-markers-panel'}>
      <UDText type={'caption-1'} color={theme.colors.Gray}>
        {t`Видеометки · ${name}`}
      </UDText>
      <UDText type={'caption-1'} color={isConnected ? theme.colors.CaseSuccess : theme.colors.warning}>
        {connectionText}
      </UDText>
      {isConnected && (
        <UDText type={'caption-1'} color={recordingMs === null ? theme.colors.warning : theme.colors.InkOnDark}>
          {recordingMs === null
            ? t`Запись не идёт`
            : position?.recordingPaused
              ? t`Запись на паузе ${recordingTime}`
              : t`● Запись ${recordingTime}`}
        </UDText>
      )}
      {isConnected && streamingMs !== null && (
        <UDText type={'caption-1'} color={theme.colors.InkOnDark}>
          {t`Эфир ${streamingTime}`}
        </UDText>
      )}
      <UDText type={'caption-1'} color={theme.colors.InkOnDark}>
        {t`События: ${eventCount} · ручные: ${manualCount} · опорные точки: ${clockCount}`}
      </UDText>
      {lastEvent && (
        <UDText type={'caption-1'} color={theme.colors.InkOnDark} data-testid={'video-markers-last-event'}>
          {t`Последнее событие: по хронометражу ${byClock}, по получению ${byReceipt}, задержка ${delay}`}
        </UDText>
      )}
      <UDButton variant={'primary'} slim disabled={!isConnected} onClick={markManual}>
        {t`⭐ Метка`}
      </UDButton>
    </div>
  );
}

export default React.memo(VideoMarkersPanel);

/** Позиция в видео как на таймере программы: `ч:мм:сс`; нет позиции — «—». */
function formatPosition(ms: number | null): string {
  if (ms === null) return '—';

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
